import React, { useState } from 'react';

import {
  Box,
  Grid,
  Typography,
  Modal,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Input,
  Backdrop,
  CircularProgress,
  Alert,
} from '@mui/material';
import { callApi } from 'src/core/callApi';
import { COLOR_PRIMARY, COLOR_RED } from 'src/utils/const';

import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import CloseIcon from '@mui/icons-material/Close';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { useDispatch } from 'react-redux';
import { setUserAsVerified } from 'src/store/Dashboard/actions';

const OTPInputBox = ({
  length = 6,
  onComplete,
  matches,
}: {
  length: number;
  onComplete: (otp: string) => void;
  matches: boolean;
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(''));
  // Auto check for OTP for once, if made mistake 2nd submission manual
  const [firstOtpAutoSubmitted, setFirstOtpAutoSubmitted] = useState<boolean>(false);

  const handleChange = (element, index: number) => {
    const value = element.value;
    if (/^\d*$/.test(value)) {
      // contains only digits (0–9)
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move focus to the next input if the current one is filled
      if (value && index < length - 1) {
        (document.getElementById(`otp-input-${index + 1}`) as HTMLElement).focus();
      }

      // Call onComplete if OTP is complete
      if (newOtp.join('').length === length) {
        if (!firstOtpAutoSubmitted) {
          onComplete(newOtp.join(''));
          setFirstOtpAutoSubmitted(true);
        }
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      (document.getElementById(`otp-input-${index - 1}`) as HTMLElement).focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pastedText = e.clipboardData.getData('Text');
    if (/^\d*$/.test(pastedText) && pastedText.length === length) {
      const newOtp = pastedText.split('').slice(0, length);
      setOtp(newOtp);

      if (!firstOtpAutoSubmitted) {
        onComplete(newOtp.join(''));
        setFirstOtpAutoSubmitted(true);
      }
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Enter OTP
      </Typography>
      <FormControl>
        <Box display="flex" gap={matches ? 0.5 : 2} justifyContent="center">
          {otp.map((data, index) => (
            <TextField
              key={index}
              type="text"
              inputProps={{
                maxLength: 1,
                style: { textAlign: 'center', fontSize: '1.5rem' },
              }}
              value={data}
              onPaste={handlePaste}
              onChange={e => handleChange(e.target, index)}
              onKeyDown={e => handleKeyDown(e, index)}
              id={`otp-input-${index}`}
              variant="outlined"
              size="small"
              sx={{ width: '50px' }}
            />
          ))}
        </Box>
      </FormControl>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        endIcon={<SendRoundedIcon sx={{ color: '#fff' }} />}
        onClick={() => onComplete(otp.join(''))}
        sx={{ mt: 3, px: 4, color: '#fff' }}
      >
        Continue
      </Button>
    </Box>
  );
};

const EmailVerifyModal = ({
  open,
  setOpen,
  email,
  matches,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  email: string;
  matches: boolean;
}) => {
  const [isEmailSentAgain, setIsEmailSentAgain] = useState<boolean>(false);
  const [isEmailSending, setIsEmailSending] = useState<boolean>(false);
  const [otpInput, setOtpInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isEstimator = window.location.pathname.includes('airbnb-calculator');

  const dispatch = useDispatch();

  const verificationEmailLambdaWrapper = async (
    params: any,
    authNeeded: boolean,
    isVerifying: boolean = false
  ) => {
    setLoading(true);
    // const url = 'https://nucx2ebwi6.execute-api.us-east-2.amazonaws.com/default/verifyUser';
    const url = 'https://djkgx4zt5g.execute-api.us-east-2.amazonaws.com/default/verifyUserTest';
    const config = {
      queryParams: params,
    };
    const resp: any = await callApi(url, config, authNeeded);
    if (resp.error) {
      setErrorMessage(resp.error.toString());
      setLoading(false);
      return;
    }
    setIsEmailSentAgain(true);
    setLoading(false);
    isVerifying && setOpen(false);
    isVerifying && dispatch(setUserAsVerified());
  };

  const sendVerificationEmail = async () => {
    verificationEmailLambdaWrapper(
      {
        action: 'generate_otp',
        email: encodeURIComponent(email),
      },
      false
    );
  };

  const handleOtpVerification = (otp: string) => {
    verificationEmailLambdaWrapper(
      {
        action: 'verify_otp',
        otp: otp,
      },
      true,
      true
    );
  };

  const handleRegenarateLink = () => {
    if (!isEmailSending) {
      setIsEmailSending(true);
      sendVerificationEmail();
    }
  };

  const handleClose = () => {
    !isEstimator && setOpen(false);
  };

  return (
    <>
      <Modal open={open} onClose={() => handleClose()}>
        <>
          <Box
            sx={{
              height: '70vh',
              width: '95vw',
              minWidth: '350px',
              maxWidth: '800px',
              overflow: 'auto',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9000,
              background: '#fff',
              borderRadius: '4px',
            }}
          >
            <Backdrop open={loading} style={{ zIndex: 1000 }}>
              <CircularProgress sx={{ color: '#fff' }} />
            </Backdrop>
            {errorMessage && (
              <Alert severity="error" onClose={() => setErrorMessage(null)}>
                Invalid OTP
              </Alert>
            )}
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              direction="column"
              spacing={2}
              sx={{ position: 'relative', marginTop: '4px' }}
            >
              <Grid item justifyContent="center">
                {isEmailSentAgain ? (
                  <MarkEmailReadIcon sx={{ fontSize: '80px', color: COLOR_PRIMARY }} />
                ) : (
                  <MarkEmailUnreadIcon sx={{ fontSize: '80px', color: COLOR_PRIMARY }} />
                )}
              </Grid>

              <Grid item justifyContent="center">
                <Typography
                  variant="h5"
                  style={{ fontWeight: 'bold', fontSize: '24px', textAlign: 'center' }}
                >
                  Please verify your email address
                </Typography>
              </Grid>

              <Grid
                item
                justifyContent="center"
                sx={{ width: 'max(70%, 280px)', marginTop: '-20px' }}
              >
                <OTPInputBox length={6} onComplete={handleOtpVerification} matches={matches} />
              </Grid>

              <Grid item justifyContent="center" sx={{ width: '70%' }}>
                <Typography
                  variant="body1"
                  sx={{ color: '#6B6B6B', fontSize: '16px', textAlign: 'center' }}
                >
                  {isEmailSentAgain
                    ? 'An email has been sent to your account. If you have already verified, please refresh the page.'
                    : 'Please use the OPT that is sent to your email in order to verify your account. If you have already verified, please refresh the page.'}
                </Typography>
              </Grid>

              {!isEmailSentAgain && (
                <Grid item justifyContent="center" sx={{ width: '70%' }}>
                  <Typography variant="body1" sx={{ color: '#6B6B6B', textAlign: 'center' }}>
                    Didn’t get the OTP?
                    <Button
                      onClick={handleRegenarateLink}
                      variant="contained"
                      sx={{
                        color: COLOR_PRIMARY,
                        marginLeft: '10px',
                        background: '#fff',
                      }}
                    >
                      Resend OTP to your email
                    </Button>
                  </Typography>
                </Grid>
              )}

              <Box
                sx={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                }}
              >
                {!isEstimator && (
                  <CloseIcon
                    onClick={() => handleClose()}
                    sx={{ fontSize: '40px', cursor: 'pointer', color: COLOR_RED }}
                  />
                )}
              </Box>
            </Grid>
          </Box>
        </>
      </Modal>
    </>
  );
};

export default EmailVerifyModal;
